import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, MapPin, MessageCircle, MessageSquare, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Eyebrow, GoldRule, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS =
  "w-full rounded-lg border border-[#E2DBCB]/15 bg-[#0C0D0E] px-4 py-3 text-sm text-[#F7F5F0] placeholder:text-[#75726B] focus:outline-none focus:border-[#D4AF37]/60 transition-colors";

const INITIAL = {
  name: "",
  salon: "",
  email: "",
  phone: "",
  request_type: "Devis salon",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé", {
        description: "L'atelier vous répond sous 24 h ouvrées.",
      });
      setForm(INITIAL);
    } catch {
      toast.error("Échec de l'envoi", {
        description: "Réessayez dans un instant.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-[#E2DBCB]/10 bg-[#141619]/40"
    >
      <div className="py-24 sm:py-32 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Eyebrow>Parlons de votre salon</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">
            Un devis sur-mesure,{" "}
            <em className="italic text-[#D4AF37]">sous 24 h</em>.
          </h2>
          <GoldRule className="mt-8" />
          <p className="mt-8 max-w-md text-base text-[#B0ABA0] leading-relaxed">
            Dotation complète de salon, échantillons de tissus, broderie aux
            initiales de votre maison ou commande sur-mesure : écrivez-nous,
            nous concevons la réponse avec vous.
          </p>
          <div className="mt-10 space-y-5 text-sm">
            <p className="flex items-center gap-4 text-[#B0ABA0]">
              <Mail size={15} className="text-[#D4AF37]" />
              <a
                data-testid="contact-email"
                href="mailto:contact@tarantina.fr"
                className="font-mono transition-colors hover:text-[#D4AF37]"
              >
                contact@tarantina.fr
              </a>
            </p>
            <div className="flex flex-wrap items-center gap-3 text-[#B0ABA0]">
              <span className="flex items-center gap-4">
                <Phone size={15} className="text-[#D4AF37]" />
                <a
                  data-testid="contact-phone"
                  href="tel:+33671190938"
                  className="font-mono transition-colors hover:text-[#D4AF37]"
                >
                  06 71 19 09 38
                </a>
              </span>
              <a
                data-testid="contact-whatsapp"
                href="https://wa.me/33671190938"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-4 py-1.5 text-xs transition-colors hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]"
              >
                <MessageCircle size={13} /> WhatsApp
              </a>
              <a
                data-testid="contact-sms"
                href="sms:+33671190938"
                className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-4 py-1.5 text-xs transition-colors hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]"
              >
                <MessageSquare size={13} /> SMS
              </a>
            </div>
            <p className="flex items-center gap-4 text-[#B0ABA0]">
              <MapPin size={15} className="text-[#D4AF37]" />
              <span data-testid="contact-address">
                Atelier Optimess — 26800 Portes-lès-Valence
              </span>
            </p>
          </div>
          <div
            data-testid="contact-notes"
            className="mt-8 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-5 text-xs leading-relaxed text-[#B0ABA0]"
          >
            <p>Tarifs nets — franchise de TVA (SASU).</p>
            <p className="mt-1.5">
              Paiement total en ligne sécurisé — ou en 2 fois sur demande par
              email.
            </p>
            <p className="mt-1.5">
              Envie d'un tissu coloré ou imprimé ? Nous vous ferons des
              propositions adaptées à votre salon.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-2xl bg-[#141619] border border-[#E2DBCB]/10 p-8 sm:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Votre nom *"
                className={INPUT_CLS}
              />
              <input
                data-testid="contact-salon-input"
                value={form.salon}
                onChange={set("salon")}
                placeholder="Nom du salon"
                className={INPUT_CLS}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email *"
                className={INPUT_CLS}
              />
              <input
                data-testid="contact-phone-input"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Téléphone"
                className={INPUT_CLS}
              />
            </div>
            <select
              data-testid="contact-type-select"
              value={form.request_type}
              onChange={set("request_type")}
              className={INPUT_CLS}
            >
              <option>Devis salon</option>
              <option>Échantillons tissus</option>
              <option>Commande sur-mesure</option>
              <option>Autre demande</option>
            </select>
            <textarea
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Parlez-nous de votre projet : nombre de fauteuils, matières envisagées, broderie… *"
              className={`${INPUT_CLS} resize-none`}
            />
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-3.5 text-sm font-semibold text-[#0C0D0E] transition-colors hover:bg-[#C5A059] disabled:opacity-60"
            >
              {sending ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Send size={15} />
              )}
              {sending ? "Envoi en cours…" : "Envoyer ma demande"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
